import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Search, Plus, Trash2, Calendar, Stethoscope, Hospital, CheckCircle, Loader2, Scan } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StableInput from './stable-input';
import DatePicker from '../../ui/date-picker';

const DiagnosticImagingForm = ({ data, onChange, onSave, isLoading, readonly = false }) => {
  const [formData, setFormData] = useState({
    surgicalHistory: [],
    hospitalization: [],
    diagnosticImaging: []
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [hospitalizationSearchTerm, setHospitalizationSearchTerm] = useState('');
  const [diagnosticImagingSearchTerm, setDiagnosticImagingSearchTerm] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState({});
  const formDataRef = useRef(formData);
  const updateTimeoutRef = useRef(null);
  formDataRef.current = formData;

  const debouncedUpdate = useCallback((updatedData) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(updatedData);
      }
    }, 100);
  }, [onChange]);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        surgicalHistory: data.surgicalHistory || [],
        hospitalization: data.hospitalization || [],
        diagnosticImaging: data.diagnosticImaging || []
      }));
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.keys(datePickerOpen).length > 0) {
        const isDatePicker = event.target.closest('.date-picker-container');
        if (!isDatePicker) {
          setDatePickerOpen({});
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [datePickerOpen]);

  const handleAddSurgicalHistory = () => {
    const newEntry = {
      id: Date.now(),
      date: '',
      surgery: ''
    };
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        surgicalHistory: [...prev.surgicalHistory, newEntry]
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleAddHospitalization = () => {
    const newEntry = {
      id: Date.now(),
      date: '',
      reason: ''
    };
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        hospitalization: [...prev.hospitalization, newEntry]
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleSurgicalHistoryChange = (id, field, value) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        surgicalHistory: prev.surgicalHistory.map(entry => 
          entry.id === id ? { ...entry, [field]: value } : entry
        )
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleHospitalizationChange = (id, field, value) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        hospitalization: prev.hospitalization.map(entry => 
          entry.id === id ? { ...entry, [field]: value } : entry
        )
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleDeleteSurgicalHistory = (id) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        surgicalHistory: prev.surgicalHistory.filter(entry => entry.id !== id)
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleDeleteHospitalization = (id) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        hospitalization: prev.hospitalization.filter(entry => entry.id !== id)
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleAddDiagnosticImaging = () => {
    const newEntry = {
      id: Date.now(),
      date: '',
      locationContrast: '',
      indication: '',
      result: ''
    };
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        diagnosticImaging: [...prev.diagnosticImaging, newEntry]
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleDiagnosticImagingChange = (id, field, value) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        diagnosticImaging: prev.diagnosticImaging.map(entry => 
          entry.id === id ? { ...entry, [field]: value } : entry
        )
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  const handleDeleteDiagnosticImaging = (id) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        diagnosticImaging: prev.diagnosticImaging.filter(entry => entry.id !== id)
      };
      
      debouncedUpdate(updatedData);
      return updatedData;
    });
  };

  // Format date from ISO to MM/DD/YY
  const formatDateToMMDDYY = (dateString) => {
    if (!dateString) return '';
    try {
      // If already in MM/DD/YY format, return as-is
      if (dateString.includes('/') && dateString.split('/').length === 3) {
        return dateString;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return as-is if not a valid date
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Parse MM/DD/YY to ISO format for DatePicker
  const parseMMDDYYToISO = (dateString) => {
    if (!dateString) return '';
    try {
      // Try to parse MM/DD/YY format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        let year = parseInt(parts[2], 10);
        // Convert 2-digit year to 4-digit (assuming 2000-2099)
        if (year < 100) {
          year = year < 50 ? 2000 + year : 1900 + year;
        }
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      // If parsing fails, try to parse as ISO or other formats
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  const handleDatePickerChange = (entryId, isoDate) => {
    const formattedDate = isoDate ? formatDateToMMDDYY(isoDate) : '';
    handleDiagnosticImagingChange(entryId, 'date', formattedDate);
    setDatePickerOpen(prev => ({ ...prev, [entryId]: false }));
  };

  const validateForm = () => {
    const errors = [];
    
    formData.surgicalHistory.forEach((entry, index) => {
      if (entry.date && !entry.surgery) {
        errors.push(`Surgery ${index + 1}: Surgery name is required when date is provided`);
      }
      if (entry.surgery && !entry.date) {
        errors.push(`Surgery ${index + 1}: Date is required when surgery name is provided`);
      }
    });
    
    formData.hospitalization.forEach((entry, index) => {
      if (entry.date && !entry.reason) {
        errors.push(`Hospitalization ${index + 1}: Reason is required when date is provided`);
      }
      if (entry.reason && !entry.date) {
        errors.push(`Hospitalization ${index + 1}: Date is required when reason is provided`);
      }
    });
    
    formData.diagnosticImaging.forEach((entry, index) => {
      if (entry.date && !entry.locationContrast && !entry.indication && !entry.result) {
        errors.push(`Diagnostic Imaging ${index + 1}: At least one field (Location/Contrast, Indication, or Result) is required when date is provided`);
      }
    });
    
    return errors;
  };

  const hasData = () => {
    const hasSurgicalData = formData.surgicalHistory.some(entry => 
      entry.procedure || entry.date || entry.surgeon || entry.reason || entry.surgery
    );
    const hasHospitalizationData = formData.hospitalization.some(entry => 
      entry.facility || entry.date || entry.reason
    );
    const hasDiagnosticImagingData = formData.diagnosticImaging.some(entry => 
      entry.date || entry.locationContrast || entry.indication || entry.result
    );
    return hasSurgicalData || hasHospitalizationData || hasDiagnosticImagingData;
  };

  const handleSave = async () => {
    if (!hasData()) {
      toast.error('Please add at least one entry before saving');
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    if (onSave) {
      await onSave(formData);
    }
  };

  const filteredSurgicalHistory = formData.surgicalHistory.filter(entry =>
    entry.surgery.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.date.includes(searchTerm)
  );

  const filteredHospitalization = formData.hospitalization.filter(entry =>
    entry.reason.toLowerCase().includes(hospitalizationSearchTerm.toLowerCase()) ||
    entry.date.includes(hospitalizationSearchTerm)
  );

  const filteredDiagnosticImaging = formData.diagnosticImaging.filter(entry =>
    (entry.locationContrast || '').toLowerCase().includes(diagnosticImagingSearchTerm.toLowerCase()) ||
    (entry.indication || '').toLowerCase().includes(diagnosticImagingSearchTerm.toLowerCase()) ||
    (entry.result || '').toLowerCase().includes(diagnosticImagingSearchTerm.toLowerCase()) ||
    (entry.date || '').includes(diagnosticImagingSearchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Surgical History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-500" />
            Surgical History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4"> 
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search surgical history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="hidden xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="xl:col-span-2">Sl No</div>
            <div className="xl:col-span-3">Date (Mo/Yr)</div>
            <div className="xl:col-span-6">Surgery</div>
            <div className="xl:col-span-1">Action</div>
          </div>

          {filteredSurgicalHistory.map((entry, index) => (
            <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 xl:grid-cols-12 xl:gap-4 xl:rounded-none xl:border-0 xl:p-0 xl:items-center">
              <div data-label="Sl No" className="xl:col-span-2 text-sm text-gray-500 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                {index + 1}
              </div>
              <div data-label="Date (Mo/Yr)" className="xl:col-span-3 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Input
                  placeholder="10/15"
                  value={entry.date}
                  onChange={(e) => handleSurgicalHistoryChange(entry.id, 'date', e.target.value)}
                  className="text-sm"
                />
              </div>
              <div data-label="Surgery" className="xl:col-span-6 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Input
                  placeholder="Right distal femur fracture repair"
                  value={entry.surgery}
                  onChange={(e) => handleSurgicalHistoryChange(entry.id, 'surgery', e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="xl:col-span-1 flex justify-end xl:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteSurgicalHistory(entry.id)}
                  disabled={readonly}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={handleAddSurgicalHistory}
            disabled={readonly}
            className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Surgical History Entry
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hospital className="h-5 w-5 text-red-500" />
            Hospitalization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search hospitalization history..."
              value={hospitalizationSearchTerm}
              onChange={(e) => setHospitalizationSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="hidden xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="xl:col-span-2">Sl No</div>
            <div className="xl:col-span-3">Date (Mo/Yr)</div>
            <div className="xl:col-span-6">Reason</div>
            <div className="xl:col-span-1">Action</div>
          </div>

          {filteredHospitalization.map((entry, index) => (
            <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 xl:grid-cols-12 xl:gap-4 xl:rounded-none xl:border-0 xl:p-0 xl:items-center">
              <div data-label="Sl No" className="xl:col-span-2 text-sm text-gray-500 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                {index + 1}
              </div>
              <div data-label="Date (Mo/Yr)" className="xl:col-span-3 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Input
                  placeholder="01/24"
                  value={entry.date}
                  onChange={(e) => handleHospitalizationChange(entry.id, 'date', e.target.value)}
                  className="text-sm"
                />
              </div>
              <div data-label="Reason" className="xl:col-span-6 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Textarea
                  placeholder="DHR-diarrhea/nausea & vomiting/influenza/UTI e. coli ESBL"
                  value={entry.reason}
                  onChange={(e) => handleHospitalizationChange(entry.id, 'reason', e.target.value)}
                  className="text-sm min-h-[40px] resize-none"
                  rows={2}
                />
              </div>
              <div className="xl:col-span-1 flex justify-end xl:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteHospitalization(entry.id)}
                  disabled={readonly}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={handleAddHospitalization}
            disabled={readonly}
            className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Hospitalization Entry
          </Button>
        </CardContent>
      </Card>

      {/* Diagnostic Imaging */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5 text-green-500" />
            Diagnostic Imaging
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search diagnostic imaging..."
              value={diagnosticImagingSearchTerm}
              onChange={(e) => setDiagnosticImagingSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="hidden xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="xl:col-span-1">SI No</div>
            <div className="xl:col-span-2">Date (MM/DD/YY)</div>
            <div className="xl:col-span-3">Location/Contrast/Name of Other Test/ Misc.</div>
            <div className="xl:col-span-1">Indication</div>
            <div className="xl:col-span-4">Result</div>
            <div className="xl:col-span-1">Action</div>
          </div>

          {filteredDiagnosticImaging.map((entry, index) => {
            const originalIndex = formData.diagnosticImaging.findIndex(e => e.id === entry.id);
            return (
              <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 xl:grid-cols-12 xl:gap-4 xl:rounded-none xl:border-0 xl:p-0 items-start">
                <div data-label="Sl No" className="xl:col-span-1 text-sm text-gray-500 xl:pt-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                  {originalIndex + 1}
                </div>
                <div data-label="Date (MM/DD/YY)" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                  <div className="relative date-picker-container">
                    <Input
                      placeholder="MM/DD/YY"
                      value={entry.date}
                      onChange={(e) => handleDiagnosticImagingChange(entry.id, 'date', e.target.value)}
                      className="text-sm pr-10"
                      disabled={readonly}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => !readonly && setDatePickerOpen(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                      disabled={readonly}
                      className="absolute right-0 top-0 h-full px-2 hover:bg-gray-100"
                    >
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </Button>
                    {datePickerOpen[entry.id] && !readonly && (
                      <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-2">
                        <DatePicker
                          value={entry.date ? parseMMDDYYToISO(entry.date) : ''}
                          onChange={(isoDate) => handleDatePickerChange(entry.id, isoDate)}
                          placeholder="MM/DD/YY"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div data-label="Location / Contrast / Other Test" className="xl:col-span-3 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                  <Textarea
                    placeholder="Location/Contrast/Name of Other Test/ Misc."
                    value={entry.locationContrast}
                    onChange={(e) => handleDiagnosticImagingChange(entry.id, 'locationContrast', e.target.value)}
                    className="text-sm min-h-[40px] resize-none"
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div data-label="Indication" className="xl:col-span-1 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                  <Textarea
                    placeholder="Indication"
                    value={entry.indication}
                    onChange={(e) => handleDiagnosticImagingChange(entry.id, 'indication', e.target.value)}
                    className="text-sm min-h-[40px] resize-none"
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div data-label="Result" className="xl:col-span-4 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                  <Textarea
                    placeholder="Result"
                    value={entry.result}
                    onChange={(e) => handleDiagnosticImagingChange(entry.id, 'result', e.target.value)}
                    className="text-sm min-h-[40px] resize-none"
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div className="xl:col-span-1 xl:pt-2 flex justify-end xl:block">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDiagnosticImaging(entry.id)}
                    disabled={readonly}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}

          <Button
            variant="outline"
            onClick={handleAddDiagnosticImaging}
            disabled={readonly}
            className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Diagnostic Imaging Entry
          </Button>
        </CardContent>
      </Card>
      {!readonly && onSave && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isLoading || readonly || !hasData()}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Diagnostic Imaging
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default React.memo(DiagnosticImagingForm);
